// src/components/ConfirmationModal.tsx

import { CreditCardOutlined, InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Space, Tooltip, Typography } from "antd";
import type React from "react";
import type { UserInfo } from "@/types";
import { trackEvent } from "@/utils/gtm";

const { Title, Text } = Typography;

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  creditCost: number | null;
  userInfo: UserInfo | null;
  onBuyCredits: () => void;
  documentInfo: {
    originalFileName: string;
    originalFileSize: number;
    totalPages: number;
  } | null;
}

const getRequiredCreditPurchase = (needed: number) => {
  const tiers = [25, 75, 175];
  return tiers.find((tier) => tier >= needed) || tiers[tiers.length - 1];
};

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + " MB";
  else return (bytes / 1073741824).toFixed(1) + " GB";
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  creditCost,
  userInfo,
  onBuyCredits,
  documentInfo,
}) => {
  const availableCredits = userInfo?.availableCredits ?? 0;
  const insufficientCredits = (creditCost || 0) > availableCredits;
  const creditDeficit = Math.max((creditCost || 0) - availableCredits, 0);
  const remainingCredits = Math.max(availableCredits - (creditCost || 0), 0);

  const handleConfirm = () => {
    console.log("Confirm button clicked");
    trackEvent("process_document_confirmed", {
      file_name: documentInfo?.originalFileName || "",
      file_size: documentInfo?.originalFileSize ?? 0,
      total_pages: documentInfo?.totalPages ?? 0,
      credit_cost: creditCost || 0,
    });
    onConfirm();
  };

  const renderCreditBar = () => {
    const minUsedPercentage = 10; // Minimum percentage for the blue part
    let usedPercentage = Math.min(((creditCost || 0) / availableCredits) * 100, 100);
    usedPercentage = Math.max(usedPercentage, minUsedPercentage);
    const remainingPercentage = Math.max(100 - usedPercentage, 0);

    return (
      <div className="w-full bg-gray-200 rounded-full h-6 sm:h-8 mb-4 sm:mb-6 relative shadow-inner">
        <div
          className="bg-blue-500 h-6 sm:h-8 rounded-l-full absolute left-0 top-0 flex items-center justify-center transition-all duration-500 ease-in-out"
          style={{ width: `${usedPercentage}%`, minWidth: "40px" }}
        >
          <span className="px-2 sm:px-4 text-white text-xs sm:text-sm leading-6 sm:leading-8 whitespace-nowrap">
            {formatNumber(creditCost || 0)}
          </span>
        </div>
        <div
          className={`h-6 sm:h-8 rounded-r-full absolute right-0 top-0 flex items-center justify-center transition-all duration-500 ease-in-out ${insufficientCredits ? "bg-red-500" : "bg-green-500"}`}
          style={{ width: `${remainingPercentage}%` }}
        >
          <span className="px-2 sm:px-4 text-white text-xs sm:text-sm leading-6 sm:leading-8 whitespace-nowrap">
            {formatNumber(remainingCredits)} credits left
          </span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className="mb-2 sm:mb-4">
          <Title level={4} className="flex items-center text-base sm:text-lg">
            <InfoCircleOutlined className="mr-2 text-blue-500" />
            Confirm Processing
          </Title>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={
        <div className="pt-2 sm:pt-4 flex flex-col sm:flex-row justify-end">
          <Button
            key="cancel"
            onClick={onClose}
            className="mb-2 sm:mb-0 sm:mr-2 hover:bg-gray-200 rounded-full"
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={handleConfirm}
            disabled={insufficientCredits}
            className="hover:shadow-lg hover:scale-105 transition-transform rounded-full"
          >
            Confirm Processing
          </Button>
        </div>
      }
      className="confirmation-modal"
      centered
    >
      <Text strong className="text-center block mb-2 sm:mb-4 text-sm sm:text-base">
        Are you sure you want to process this document?
      </Text>

      {documentInfo && (
        <div className="mt-2 sm:mt-4 mb-4 sm:mb-6 text-center">
          <Text className="text-sm sm:text-base">File: </Text>
          <Text strong className="text-sm sm:text-base">
            {documentInfo.originalFileName}
          </Text>
          <br />
          <Text className="text-sm sm:text-base">Size: </Text>
          <Text strong className="text-sm sm:text-base">
            {formatFileSize(documentInfo.originalFileSize)}
          </Text>
          <br />
          <Text className="text-sm sm:text-base">Pages: </Text>
          <Text strong className="text-sm sm:text-base">
            {documentInfo.totalPages}
          </Text>
        </div>
      )}

      <div className="mt-2 sm:mt-4 mb-4 sm:mb-6 text-center">
        <Text className="text-sm sm:text-base">This will consume </Text>
        <Text strong className="text-lg sm:text-xl text-blue-600">
          {formatNumber(creditCost || 0)} credits
        </Text>
        <Text className="text-sm sm:text-base"> upon successful completion.</Text>
      </div>

      {userInfo && (
        <Space direction="vertical" className="w-full">
          <div className="flex justify-between items-center">
            <Text className="text-sm sm:text-base">Available Credits:</Text>
            <Text strong className="text-lg sm:text-xl">
              {formatNumber(availableCredits)}
            </Text>
          </div>
          {renderCreditBar()}
          {!insufficientCredits && (
            <Text className="text-green-600 text-center block mt-2 text-sm sm:text-base">
              You will have {formatNumber(remainingCredits)} credits remaining after processing.
            </Text>
          )}
        </Space>
      )}

      {insufficientCredits && (
        <Alert
          message="Insufficient Credits"
          description={
            <>
              <Text className="text-red-500 text-sm sm:text-base">
                You need {formatNumber(creditDeficit)} more credits to process this document.
              </Text>
              <div className="mt-2">
                <Text className="text-sm sm:text-base">Recommended purchase: </Text>
                <Tooltip title="This is the minimum credit pack that covers your needs">
                  <Text strong className="text-blue-600 text-sm sm:text-base">
                    {formatNumber(getRequiredCreditPurchase(creditDeficit))} credits
                  </Text>
                </Tooltip>
              </div>
            </>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          action={
            <Tooltip title="Purchase more credits">
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={onBuyCredits}
                className="mt-2 sm:mt-0 rounded-full hover:bg-blue-600 transition-colors text-sm sm:text-base"
              >
                Buy Credits
              </Button>
            </Tooltip>
          }
          className="rounded-lg mt-4 shadow-sm"
        />
      )}
    </Modal>
  );
};

export default ConfirmationModal;
